import { useTranslation } from "react-i18next";
import { ActionIcon, Alert, Box, Button, Group, NumberInput, Select,Stack, Text, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconAlertCircle,IconPlus, IconTrash } from "@tabler/icons-react";
import { z } from "zod";

import { useUpdateMySession } from "@hooks/mutations/onboarding/useUpdateMySession";


const schema = z.object({
  services: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().min(2, "Service name is required"),
      category: z.enum(["teleconsultation", "homecare"]),
      price: z.number().min(0, "Price cannot be negative"),
      currency: z.string().length(3),
      duration_minutes: z.number().min(5).max(480),
    })
  ).min(1, "You must add at least one service to your catalogue"),
});

type Inputs = z.infer<typeof schema>;

interface Step4Props {
  sessionData: any;
}

export const Step4Services = ({ sessionData }: Step4Props) => {
  const { t } = useTranslation();
  const { mutate: updateSession, isLoading: isSaving } = useUpdateMySession();
  const enabledCategories = sessionData.consultation_modes?.length
    ? sessionData.consultation_modes
    : ["teleconsultation"];
  const defaultCategory = enabledCategories[0];

  const form = useForm<Inputs>({
    validate: zodResolver(schema),
    initialValues: {
      services: sessionData.services?.length
        ? sessionData.services.map((service: any) => ({
            ...service,
            category: service.category || defaultCategory,
            currency: service.currency || "XOF",
            duration_minutes: service.duration_minutes || service.duration || 30,
          }))
        : [{ name: "", category: defaultCategory, price: 0, currency: "XOF", duration_minutes: 30 }],
    },
  });

  const handleSaveAndContinue = (values: Inputs) => {
    updateSession({
      services: values.services,
      current_step: 5, // Advance to next step (Availability)
    });
  };

  const handleSaveDraft = () => {
    const values = form.getValues();
    updateSession({
      services: values.services,
    });
  };

  const handleBack = () => {
    updateSession({ current_step: 3 });
  };

  return (
    <form onSubmit={form.onSubmit(handleSaveAndContinue)}>
      <Stack gap="lg">
        <Alert color="blue" icon={<IconAlertCircle size={16} />}>
          {t("onboarding-add-services-desc")}
        </Alert>

        <Box>
          {form.getValues().services.map((_, index) => (
            <Group key={index} align="flex-end" mb="sm" wrap="wrap" style={{ border: "1px solid #e9ecef", padding: "16px", borderRadius: "8px", background: "#f8f9fa" }}>
              <TextInput
                label={t("onboarding-service-name")}
                placeholder={t("onboarding-service-placeholder")}
                withAsterisk
                style={{ flex: 2 }}
                key={form.key(`services.${index}.name`)}
                {...form.getInputProps(`services.${index}.name`)}
              />
              <Select
                label={t("onboarding-service-category", "Service category")}
                data={[
                  { value: "teleconsultation", label: t("teleconsultation", "Teleconsultation") },
                  { value: "homecare", label: t("homecare", "Homecare") },
                ].filter(option => enabledCategories.includes(option.value))}
                withAsterisk
                style={{ flex: 1 }}
                key={form.key(`services.${index}.category`)}
                {...form.getInputProps(`services.${index}.category`)}
              />
              <NumberInput
                label={`${t("onboarding-price")} (XOF)`}
                placeholder={t("onboarding-price-placeholder")}
                withAsterisk
                min={0}
                step={1000}
                style={{ flex: 1 }}
                key={form.key(`services.${index}.price`)}
                {...form.getInputProps(`services.${index}.price`)}
              />
              <NumberInput
                label={t("onboarding-duration", "Duration (minutes)")}
                min={5}
                max={480}
                step={5}
                style={{ flex: 1 }}
                key={form.key(`services.${index}.duration_minutes`)}
                {...form.getInputProps(`services.${index}.duration_minutes`)}
              />
              <ActionIcon
                color="red"
                variant="subtle"
                size="lg"
                mb={4}
                onClick={() => form.removeListItem("services", index)}
                disabled={form.getValues().services.length <= 1}
                title={t("onboarding-remove-service")}
              >
                <IconTrash size={20} />
              </ActionIcon>
            </Group>
          ))}

          {form.errors.services && (
            <Text c="red" size="sm" mt="sm">
              {form.errors.services as string}
            </Text>
          )}

          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            mt="md"
            onClick={() => form.insertListItem("services", {
              name: "",
              category: defaultCategory,
              price: 0,
              currency: "XOF",
              duration_minutes: 30,
            })}
          >
            {t("onboarding-add-service")}
          </Button>
        </Box>

        <Group justify="space-between" mt="xl">
          <Button variant="outline" onClick={handleBack} loading={isSaving} type="button">
            {t("onboarding-back")}
          </Button>
          <Group>
            <Button variant="outline" onClick={handleSaveDraft} loading={isSaving} type="button">
              {t("onboarding-save-draft")}
            </Button>
            <Button type="submit" loading={isSaving} size="md">
              {t("onboarding-save-continue")}
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};
